import './CopyAttachmentsExecutor.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { MdOutlineInsertDriveFile } from 'react-icons/md';
import {
    IAttachmentToCopyExtended,
    ICopyAttachmentsResult,
} from '@models/document/actionsExecution/ICopyAttachmentsModel';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import Modal from '@srm/ui/modal';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import ModalLoadingOverlay from '@srm/ui/modal-loading-overlay';
import Hint from '@srm/ui/hint';
import SelectableList from '@srm/ui/selectable-list';
import { ICopyAttachmentsExecutorProps } from './ICopyAttachmentsExecutorProps';

export const CopyAttachmentsExecutorModal: React.FC<ICopyAttachmentsExecutorProps> = (
    props: ICopyAttachmentsExecutorProps,
) => {
    const [errorText, setErrorText] = useState<string>();
    // Подготовка (список файлов) и копирование — разные состояния: подготовку можно бросить
    // закрытием окна, копирование — нет.
    const [loading, setLoading] = useState<boolean>(true);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const isBusy = loading || isSubmitting;
    const [attachmentsModel, setAttachmentsModel] = useState<IAttachmentToCopyExtended[]>([]);
    // Ключ вложения — пара «документ + вложение»: attachId уникален внутри документа
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const service = new DocumentExecutionService(props.docId);

    const close = () => props.completeHandler?.(false);

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setLoading(true);
        service
            .getCopyAttachmentsModel(props.actionKey)
            .then((res) => {
                if (!ignore) setAttachmentsModel(res.data.attachments);
            })
            .catch((error) => {
                if (!ignore) setErrorText(error);
            })
            .finally(() => {
                if (!ignore) setLoading(false);
            });
        return () => {
            ignore = true;
        };
    }, []);

    const attachmentKey = (attachment: IAttachmentToCopyExtended) => `${attachment.docId}/${attachment.attachId}`;

    const items = useMemo(
        () =>
            attachmentsModel.map((attachment) => ({
                key: attachmentKey(attachment),
                title: attachment.displayName,
                subtitle: attachment.categoryName,
                icon: <MdOutlineInsertDriveFile size={18} />,
            })),
        [attachmentsModel],
    );

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={props.modalSize}
            onClose={close}
            // Любой выбранный файл — несохранённый выбор.
            hasUnsavedInput={() => selectedKeys.length > 0}
            closeDisabled={isSubmitting}
            header={props.title}
            footer={
                <div className="modal-dlg-buttons">
                    <Button
                        disabled={selectedKeys.length === 0 || isBusy}
                        size="sm"
                        aria-label={props.okButtonText}
                        onClick={() => {
                            setErrorText(undefined);
                            setIsSubmitting(true);

                            const selected = new Set(selectedKeys);
                            const selectedAttachments = attachmentsModel
                                .filter((attachment) => selected.has(attachmentKey(attachment)))
                                .map(({ docId, attachId, categoryName }) => ({ docId, attachId, categoryName }));

                            const copyAttachmentsResult: ICopyAttachmentsResult = {
                                attachments: selectedAttachments,
                            };
                            service
                                .copyAttachments(props.actionKey, copyAttachmentsResult)
                                .then(() => {
                                    if (props.completeHandler) props.completeHandler(true);
                                })
                                .catch((error) => setErrorText(error))
                                .finally(() => {
                                    setIsSubmitting(false);
                                });
                        }}
                    >
                        {props.okButtonText}
                    </Button>

                    <Button
                        variant="light"
                        size="sm"
                        aria-label={props.cancelButtonText}
                        onClick={close}
                        disabled={isSubmitting}
                    >
                        {props.cancelButtonText}
                    </Button>
                </div>
            }
        >
            <ModalLoadingOverlay visible={isBusy}>
                <div>
                    {errorText && <Hint role="alert" title={`Ошибка: ${errorText}`} tone="danger" maxWidth={'100%'} />}

                    <SelectableList
                        items={items}
                        selected={selectedKeys}
                        onChange={setSelectedKeys}
                        allowSelectAll
                        emptyText="Файлы отсутствуют"
                        ariaLabel="Файлы для копирования"
                    />
                </div>
            </ModalLoadingOverlay>
        </Modal>
    );
};
